/**
 * Предупреждение о несохранённом тексте при уходе.
 *
 * Закрытие вкладки браузер перехватывает сам — beforeunload в BookEditorV2EditableView.
 * Переходов ВНУТРИ приложения он не видит, а именно они и уничтожали работу: выбор другой
 * книги в меню, ссылки «Приобрести» и «История расходов», удаление учётной записи, выход из
 * профиля. Каждый из них размонтирует редактор, а несохранённый текст живёт только в памяти.
 *
 * Автосохранения рукописи нет намеренно (CR-3: единственная долговечная запись — кнопка
 * «Сохранить»), поэтому терялось всё, написанное с последнего сохранения. Час работы уходил
 * по одному клику и без единого вопроса — при том, что от закрытия вкладки защита стояла.
 * Найдено аудитом 2026-08-29.
 *
 * Почему хранилище — ref, а не состояние: значение читают только в момент клика, и ни один
 * экран не должен перерисовываться от того, что в рукописи появилась правка.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

type UnsavedChangesApi = {
  /** Редактор объявляет о несохранённом тексте; null — уходить можно молча. */
  setUnsavedChanges: (message: string | null) => void;
  /** true — уходить можно (нечего терять либо человек согласился потерять). */
  confirmLeave: () => boolean;
};

const UnsavedChangesContext = createContext<UnsavedChangesApi | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const messageRef = useRef<string | null>(null);

  const api = useMemo<UnsavedChangesApi>(
    () => ({
      setUnsavedChanges: (message: string | null) => {
        messageRef.current = message;
      },
      confirmLeave: () => {
        const message = messageRef.current;

        if (!message) {
          return true;
        }

        // Отметку НЕ снимаем при согласии: снять её должен уход редактора со сцены. Иначе
        // между согласием и переходом остаётся окно, в котором текст ещё не сохранён, а
        // защиты уже нет, — и следующий переход пройдёт молча.
        return window.confirm(message);
      },
    }),
    [],
  );

  return <UnsavedChangesContext.Provider value={api}>{children}</UnsavedChangesContext.Provider>;
}

export function useUnsavedChangesGuard(): UnsavedChangesApi {
  const api = useContext(UnsavedChangesContext);

  if (!api) {
    // Молчаливая заглушка здесь означала бы ровно ту потерю текста, ради которой всё это
    // написано: экран уехал бы без вопроса, и никто бы не заметил. Лучше упасть на сборке.
    throw new Error('useUnsavedChangesGuard: UnsavedChangesProvider не найден выше по дереву');
  }

  return api;
}

/**
 * Держит отметку о несохранённом тексте, пока условие верно, и обязательно снимает её при
 * уходе редактора со сцены — иначе вопрос задавался бы уже после того, как терять нечего.
 */
export function useRegisterUnsavedChanges(active: boolean, message: string): void {
  const { setUnsavedChanges } = useUnsavedChangesGuard();

  useEffect(() => {
    setUnsavedChanges(active ? message : null);

    return () => {
      setUnsavedChanges(null);
    };
  }, [active, message, setUnsavedChanges]);
}

/**
 * Спрашивает и, при отказе, не пускает дальше; для ссылки заодно отменяет переход браузера.
 *
 * Событие идёт первым аргументом, а не через замыкание вокруг обработчика: обработчик,
 * созданный во время отрисовки, читает ref — и линтер справедливо на это ругается. Здесь всё
 * происходит уже внутри обработчика клика.
 */
export function useGuardedAction(): (
  event: { preventDefault: () => void } | undefined,
  run: () => void,
) => void {
  const { confirmLeave } = useUnsavedChangesGuard();

  return useCallback(
    (event: { preventDefault: () => void } | undefined, run: () => void) => {
      if (!confirmLeave()) {
        event?.preventDefault();
        return;
      }

      run();
    },
    [confirmLeave],
  );
}
